import React, { useState, useEffect, useRef, useMemo } from 'react'
import MaterialTable, { MTableAction, MTableToolbar } from 'material-table'
import { compareTwoObjs } from '../../../services/lib/utils/helpers'
import CommonButton from '../others/common-button'
import { Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ApiRequest } from '../../../services/api/api'

// Jadcup品牌色系
const useStyles = makeStyles({
  tableContainer: {
    width: '100%',
    margin: 0,
    padding: 0,
    // 小屏幕上添加少量边距，大屏幕上紧贴屏幕
    '@media (max-width: 768px)': {
      padding: '0 8px',
    },
    // 强制显示滚动条轨道，避免抖动
    '& .MuiTableContainer-root': {
      overflowX: 'auto',
      overflowY: 'auto',
      scrollbarGutter: 'stable', // 现代浏览器预留滚动条空间
      // 自定义滚动条样式
      '&::-webkit-scrollbar': {
        height: '8px',
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f8e9',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#8bc34a',
        borderRadius: '4px',
        '&:hover': {
          background: '#7cb342',
        },
      },
    },
    '& .MuiPaper-root': {
      border: '1px solid #e3ecdf',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(34, 59, 42, 0.06), 0 1px 2px rgba(34, 59, 42, 0.04)',
      // overflow: 'auto',
    },
    '& .MuiToolbar-root': {
      background: 'linear-gradient(135deg, #eef5e3 0%, #f9fbf4 60%, #f4f8ec 100%)',
      borderBottom: '1px solid #dde8cf',
      padding: '14px 24px',
      '& button': {
        color: '#1A3C34',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#8bc34a',
          color: '#ffffff',
          // transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(139, 195, 74, 0.3)',
        },
      },
    },
    '& .MuiTableHead-root': {
      background: '#f4f8ec',
      '& .MuiTableCell-head': {
        color: '#223b2a',
        fontWeight: 700,
        fontSize: '0.8125rem',
        letterSpacing: '0.3px',
        borderBottom: '1px solid #dfe9d3',
        padding: '12px 16px',
        whiteSpace: 'nowrap',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        transition: 'all 0.2s ease',
        // 只在非编辑状态下启用 hover table row 效果
        '&:hover': {
          backgroundColor: '#DDEED2',
          // transform: 'scale(1.001)',
        },
      },
      // 编辑行禁用所有 hover 效果（关键修复）
      '& tr[mode="update"], & tr[mode="add"]': {
        backgroundColor: 'transparent !important',
        transform: 'none !important',
        '&:hover': {
          backgroundColor: 'transparent !important',
          transform: 'none !important',
        },
        '& .MuiTableCell-root': {
          backgroundColor: 'transparent !important',
        },
      },
      '& .MuiTableCell-root': {
        borderBottom: '1px solid #e8f5e9',
        padding: '14px 16px',
      },
    },
    '& .MuiTablePagination-root': {
      borderTop: '1px solid #e8f5e9',
      background: '#fafafa',
    },
    '& .MuiIconButton-root': {
      color: '#1A3C34',
      transition: 'all 0.2s ease',
      borderRadius: '8px',
      padding: '8px',
      '&:hover': {
        backgroundColor: '#8bc34a',
        color: '#ffffff',
        // transform: 'scale(1.05)',
      },
    },
    '& .MuiTableCell-root .MuiIconButton-root': {
      margin: '0 4px',
      background: '#f1f8e9',
      '&:hover': {
        background: '#8bc34a',
        color: '#2b0707ff',
        boxShadow: '0 2px 8px rgba(139, 195, 74, 0.3)',
      },
    },
    '& .MuiCheckbox-root': {
      color: '#8bc34a',
      '&.Mui-checked': {
        color: '#689f38',
      },
    },
    '& .MuiTableSortLabel-root': {
      color: '#1A3C34',
      '&:hover': {
        color: '#0d261f',
      },
      '&.MuiTableSortLabel-active': {
        color: '#1A3C34',
      },
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#c5e1a5',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#8bc34a',
    },
    '& .MuiInputBase-input': {
      color: '#558b2f',
    },
    // 修复下拉列表样式
    '& .MuiSelect-root': {
      minWidth: '100px',
    },
    '& .MuiTableCell-root .MuiSelect-select': {
      paddingRight: '24px',
    },
  },
  headerFields: {
    display: 'flex',
    gap: '16px',
    padding: '12px 24px',
    background: '#ffffff',
    borderTop: '1px solid #e8f5e9',
    '& > div': {
      padding: '8px 16px',
      borderRadius: '20px',
      background: '#f1f8e9',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#558b2f',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        background: '#8bc34a',
        color: '#ffffff',
        // transform: 'translateY(-2px)',
        boxShadow: '0 4px 8px rgba(139, 195, 74, 0.2)',
      },
      '& a': {
        color: 'inherit',
        textDecoration: 'none',
      },
    },
  },
  // 全局下拉菜单样式（防止抖动）
  '@global': {
    '.MuiMenu-paper': {
      border: '1px solid #e8f5e9 !important',
      borderRadius: '8px !important',
      boxShadow: '0 4px 12px rgba(139, 195, 74, 0.15) !important',
      maxHeight: '300px !important',
      // 关键：提高 z-index，确保菜单在最上层，不触发下方元素的 hover
      zIndex: '9999 !important',
      // 防止菜单影响下方表格行
      pointerEvents: 'auto !important',
      // 防止菜单内容抖动
      '& .MuiMenu-list': {
        padding: '4px !important',
        // 强制显示滚动条
        overflowY: 'auto !important',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f8e9',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#8bc34a',
          borderRadius: '4px',
          '&:hover': {
            background: '#7cb342',
          },
        },
      },
      '& .MuiMenuItem-root': {
        fontSize: '0.9rem !important',
        padding: '8px 16px !important',
        margin: '2px 0 !important',
        borderRadius: '6px !important',
        transition: 'all 0.2s ease !important',
        '&:hover': {
          backgroundColor: '#f1f8e9 !important',
          // transform: 'translateX(4px) !important',
        },
        '&.Mui-selected': {
          backgroundColor: '#e8f5e9 !important',
          color: '#558b2f !important',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: '#dcedc8 !important',
          },
        },
      },
    },
    // 修复 Popover 定位抖动
    '.MuiPopover-root': {
      // 提高 z-index，确保在最上层
      zIndex: '9999 !important',
      '& .MuiPaper-root': {
        // transformOrigin: 'top left !important',
      },
    },
    // 当下拉菜单打开时，添加遮罩层防止触发表格行 hover
    '.MuiBackdrop-root': {
      backgroundColor: 'transparent !important',
      pointerEvents: 'none !important',
    },
  },
})

export interface CommonTablePagePropsInterface {
  urlInfoKey?: string
  getAllUrl?: string
  title?: any // 支持字符串或 React 元素(如标题+筛选控件同排)
  column?: any
  isNotDeletable?: boolean
  isNotEditable?: boolean
  isNotAddable?: boolean
  actionButtons?: any
  addRowButtonName?: string
  mappingUpdateData?: any
  mappingRenderData?: any
  restRequestOptions?: any
  triggerResetData?: any
  notAllowUpdate?: boolean
  setRowData?: any
  rowData?: any
  columnsButton?:any
  addQuotation?: boolean
  isShowSpinnerOnInit?: boolean
  isEnableSelect?: boolean
  onSelectionChange?: any
  onFilterChange?: any
  onSearchChange?:any
  defaultPageSize?: number
  defaultPageSizeOptions?: any
  selectionProps? : any
  rowStyle?:any
  tableRef?: any // 允许外部传入 ref 来访问 tableRef
  headerFields?: any
  toolbarExtra?: any // 渲染在工具栏下方的自定义筛选栏内容
  isExportable?: boolean
  externalData?: any // 外部管理的数据，用于自定义搜索等功能
}

export default function CommonTablePageWithSortPreserve(props: CommonTablePagePropsInterface) {
  const classes = useStyles()

  // API相关状态
  const [tableData, setTableData] = useState<any>()
  const [column, setColumn] = useState(props.column)

  // 创建内部 ref
  const internalTableRef: any = useRef<any>()
  // 如果外部传入了 ref，使用外部的；否则使用内部的
  const tableRef = props.tableRef || internalTableRef

  // === 排序保存逻辑 ===
  // 存储排序状态：按字段名而非索引
  const sortState = useRef<{
    field: string | null
    direction: 'asc' | 'desc' | null
  }>({
    field: null,
    direction: null
  })

  // 监听排序变化，保存当前排序状态
  useEffect(() => {
    if (!tableRef.current) return

    const handleSortChange = (orderBy: number, orderDirection: 'asc' | 'desc') => {
      const state = tableRef.current?.state
      if (state && state.columns && state.columns[orderBy]) {
        sortState.current = {
          field: state.columns[orderBy].field,
          direction: orderDirection
        }
        console.log('Sort saved:', { field: state.columns[orderBy].field, direction: orderDirection })
      }
    }

    // 保存原始的排序回调
    const originalCallback = tableRef.current.onColumnSortChanged
    tableRef.current.onColumnSortChanged = handleSortChange

    // 初始化：如果已有排序，保存它
    const state = tableRef.current?.state
    if (state && state.orderBy !== undefined && state.orderBy !== -1 && state.columns[state.orderBy]) {
      sortState.current = {
        field: state.columns[state.orderBy].field,
        direction: state.orderDirection
      }
    }

    return () => {
      // 清理：恢复原始回调
      if (tableRef.current && originalCallback) {
        tableRef.current.onColumnSortChanged = originalCallback
      }
    }
  }, [tableRef])

  // 当数据或列变化后，恢复排序
  useEffect(() => {
    if (!tableRef.current || !sortState.current.field) return

    // 延迟执行，确保MaterialTable已处理完列更新
    const timer = setTimeout(() => {
      try {
        const state = tableRef.current?.state
        if (!state || !state.columns) return

        // 查找排序字段对应的列索引
        const sortedColIndex = state.columns.findIndex((col: any) => col.field === sortState.current.field)

        if (sortedColIndex === -1) return // 列不存在，无法恢复

        // 检查当前排序是否已丢失
        const currentSortField = state.orderBy !== -1 && state.columns[state.orderBy]
          ? state.columns[state.orderBy].field
          : null

        if (currentSortField !== sortState.current.field) {
          console.log('Restoring sort:', { field: sortState.current.field, index: sortedColIndex, direction: sortState.current.direction })

          // 恢复排序：直接设置state
          tableRef.current.setState({
            ...state,
            orderBy: sortedColIndex,
            orderDirection: sortState.current.direction || 'asc'
          })
        }
      } catch (error) {
        console.error('Failed to restore sort:', error)
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [tableData, column])

  // 数据加载逻辑（来自CommonTablePage）
  useEffect(() => {
    let isCancelled = false

    // 如果提供了外部数据，直接使用外部数据
    if (props.externalData) {
      setTableData(props.externalData)
      return
    }

    const loadData = async () => {
      try {
        const requestData: any = {
          type: 'get',
          isShowSpinner: true,
          urlInfoKey: props.urlInfoKey,
          callBackFunction: async (data: any) => {
            const processedData = props.mappingRenderData ? await props.mappingRenderData(data) : data
            setTableData(processedData)
          }
        }

        if (props.getAllUrl) {
          requestData.getAllUrl = props.getAllUrl
        }

        if (props.restRequestOptions) {
          Object.assign(requestData, props.restRequestOptions)
        }

        await ApiRequest(requestData)
      } catch (error) {
        console.error('Failed to load data:', error)
      }
    }

    if (!isCancelled) {
      loadData()
    }

    return () => {
      isCancelled = true
    }
  }, [props.triggerResetData, props.getAllUrl, props.urlInfoKey, props.externalData])

  // 更新数据的逻辑
  const updateData = async (newData: any[], dataDetail: any, type: string) => {
    try {
      dataDetail = props.mappingUpdateData ? await props.mappingUpdateData(dataDetail, type) : dataDetail

      if (dataDetail === 'resolve') {
        return Promise.resolve()
      }

      if (!dataDetail) {
        return Promise.reject()
      }

      // 发送API请求
      const requestData: any = {
        type,
        data: dataDetail,
        urlInfoKey: props.urlInfoKey,
        callBackFunction: async (data: any) => {
          const processedData = props.mappingRenderData ? await props.mappingRenderData(data) : data
          setTableData(processedData)
        }
      }

      if (props.getAllUrl) {
        requestData.getAllUrl = props.getAllUrl
      }

      if (props.restRequestOptions) {
        Object.assign(requestData, props.restRequestOptions)
      }

      return await ApiRequest(requestData)
    } catch (error) {
      console.error('Failed to update data:', error)
      return Promise.reject()
    }
  }

  // 计算是否可编辑
  const isTableEditable = !props.isNotEditable
  const isTableAddable = !props.isNotAddable
  const isTableDeletable = !props.isNotDeletable
  const actionButtons = props.actionButtons?.length ? props.actionButtons : []
  const addRowButtonTooltip = 'Add'
  const addRowButtonName = props.addRowButtonName || 'Add new '

  // 使用 useMemo 缓存 options 对象
  const tableOptions = useMemo(() => ({
    toolbarButtonAlignment: 'left' as const,
    pageSizeOptions: props.defaultPageSizeOptions || [5, 10, 30, 50, 100],
    pageSize: props.defaultPageSize || 100,
    exportButton: props.isExportable ? true : false,
    emptyRowsWhenPaging: false,
    filtering: true,
    search: true,
    debounceInterval: 0, // 禁用防抖，让搜索立即响应
    actionsColumnIndex: -1,
    addRowPosition: 'first' as const,
    headerStyle: {
      fontSize: '1rem',
      color: '#1A3C34',
      fontWeight: 600,
    },
    doubleHorizontalScroll: true,
    draggable: true,
    selection: props.isEnableSelect,
    paginationType: 'stepped' as const,
    selectionProps: props.selectionProps,
    rowStyle: (rowData: any) => {
      const baseStyle = props.rowStyle ? props.rowStyle(rowData) : {}
      return {
        ...baseStyle,
        fontSize: '0.9rem',
      }
    },
    columnsButton: props.columnsButton,
    sorting: true, // 总是启用排序
  }), [props.defaultPageSizeOptions, props.defaultPageSize, props.isExportable, props.isEnableSelect, props.selectionProps, props.rowStyle, props.columnsButton])

  // 使用 useMemo 缓存 localization 对象
  const tableLocalization = useMemo(() => ({
    header: {
      actions: 'Actions'
    },
    body: {
      addTooltip: addRowButtonTooltip
    }
  }), [addRowButtonTooltip])

  return (
    <div className={classes.tableContainer}>
      <MaterialTable
        tableRef={tableRef}
        title={
          props.toolbarExtra ? (
            <div className="jc-title-row">
              <span className="jc-title-text">{props.title}</span>
              {props.toolbarExtra}
            </div>
          ) : props.title
        }
        columns={column}
        data={tableData || []}
        options={tableOptions}
        onSelectionChange={(rows) => {
          if (props.onSelectionChange) {
            props.onSelectionChange(rows)
          }
        }}
        onFilterChange={() => {
          if (props.onFilterChange)
            props.onFilterChange(tableRef)
        }}
        onSearchChange ={(searchText: string) => {
          // 调用自定义搜索处理（如果提供）
          if (props.onSearchChange) {
            props.onSearchChange(searchText)

            // 搜索完成后，重新聚焦到搜索输入框
            setTimeout(() => {
              const searchInput = document.querySelector('[aria-label="Search"]') as HTMLInputElement ||
                                 document.querySelector('input[placeholder="Search"]') as HTMLInputElement ||
                                 document.querySelector('.MuiInputBase-input') as HTMLInputElement

              if (searchInput) {
                searchInput.focus()
                // 将光标移动到文本末尾
                const len = searchInput.value.length
                searchInput.setSelectionRange(len, len)
              }
            }, 50) // 短暂延迟确保 DOM 更新完成

            // 返回 false 阻止 Material Table 的默认搜索行为
            return false
          }
          // 不返回 false，让 Material Table 继续处理
        }}
        onChangePage={() => {
          window.scrollTo(0, 0);
        }}
        localization={tableLocalization}
        actions={actionButtons}
        components={{
          Action: (propsAction: any) => {
            const onBnClick = (event: any) => propsAction.action.onClick(event, propsAction.data)
            const bnProps: any = {onClick: (event: any) => onBnClick(event)}
            // Override action buttons
            for (let i = 0; i < actionButtons.length; i++) {
              if (propsAction.action.tooltip === actionButtons[i].tooltip) {
                const restProps = {...bnProps, [actionButtons[i].icon || 'block']: true}
                return <CommonButton name={actionButtons[i].tooltip} restProps={restProps} />
              }
            }
            // Override "Add Row Button"
            if (propsAction.action.tooltip === addRowButtonTooltip) {
              const addBnProps: any = {...bnProps}
              const state = tableRef.current?.state
              if (!isTableAddable || !!state?.showAddRow || !!state?.lastEditingRow) {
                addBnProps.style = {display: 'none'}
              }
              return <CommonButton name={addRowButtonName} restProps={addBnProps} />
            }
            return <MTableAction {...propsAction}/>
          },
          OverlayLoading: () => null,
          Container: props => <Paper {...props} elevation={0} style={{border: '1px solid #e8f5e9', borderRadius: '12px'}}/>,
          Toolbar: toolbarProps => {
            return (
              <div className="jc-toolbar-wrap">
                <MTableToolbar {...toolbarProps} />
                {props.headerFields && (
                  <div className={classes.headerFields}>
                    {props.headerFields.map((data: any, key: any) => {
                      return (
                        <div key={key}>
                          {data.click ? (
                            <a onClick={() => data.click(data.name)}>{data.name}</a>
                          ) : (
                            data.name
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }
        }}
        editable={{
          isEditHidden: _ => !isTableEditable,
          isDeleteHidden: _ => !isTableDeletable,
          isEditable: _ => isTableEditable,
          isDeletable: _ => isTableDeletable,
          onRowAdd: (newData) => new Promise((resolve, reject) => {
            if (!props.notAllowUpdate) {
              updateData([...(tableData || []), newData], newData, 'create')
                .then((_: any) => {
                  resolve('')
                })
                .catch((_: any) => {
                  reject('')
                })
            } else {
              if (!tableData) {
                setTableData([newData])
              } else {
                setTableData([...tableData, newData])
              }
              resolve('')
            }
          }),
          onRowDelete: (oldData: any) => new Promise((resolve, reject) => {
            const dataDelete = [...(tableData || [])]
            const index = oldData.tableData.id
            dataDelete.splice(index, 1)
            if (!props.notAllowUpdate) {
              updateData(dataDelete, oldData, 'delete')
                .then((_: any) => {
                  setTableData(dataDelete)
                  resolve('')
                })
                .catch((_: any) => {
                  reject('')
                })
            } else {
              setTableData(dataDelete)
              resolve('')
            }
          }),
          onRowUpdate: async (newData, oldData) => new Promise((resolve, reject) => {
            const isNothingChange = compareTwoObjs({ ...newData, tableData: null }, { ...oldData, tableData: null })
            if (isNothingChange) {
              reject('')
            } else {
              const dataUpdate = [...(tableData || [])]
              const index = oldData.tableData.id
              dataUpdate[index] = newData
              if (!props.notAllowUpdate) {
                updateData(dataUpdate, newData, 'update')
                  .then((_: any) => {
                    setTableData(dataUpdate)
                    resolve('')
                  })
                  .catch((_: any) => {
                    reject('')
                  })
              } else {
                setTableData(dataUpdate)
                resolve('')
              }
            }
          }),
        }}
      />
    </div>
  )
}
